export const STARTERS = [
  { id: "invites", label: "Let admins invite team members", prompt: "Let admins invite team members" },
  { id: "checkout", label: "Fix checkout failures when a card is declined", prompt: "Fix checkout failures when a card is declined" },
];

export const INITIAL_PROMPT =
  "What would you like your engineer to build or fix? It does not need to be perfectly written.";

const MAX_CHARS = 500;

const SECRET = [
  /\bsk-[A-Za-z0-9]{10,}\b/,
  /\bghp_[A-Za-z0-9]{20,}\b/,
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /\bpassword\s*[:=]\s*\S+/i,
  /\bmongodb(\+srv)?:\/\//i,
];
const URLS = [/https?:\/\/\S+/i, /\bgithub\.com\/\S+/i, /\bpull\/\d+/i];

export function inspectLandingDemoInput(raw) {
  const text = String(raw || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  if (!text) return { ok: false, code: "EMPTY_INPUT", message: "Enter a short description in plain text." };
  if (text.length > MAX_CHARS) return { ok: false, code: "TOO_LONG", message: "Keep this preview to 500 characters." };
  if (SECRET.some((pattern) => pattern.test(raw) || pattern.test(text))) {
    return {
      ok: false,
      code: "SECRET_REJECTED",
      message:
        "This preview is not designed for confidential information. Remove credentials or sensitive data and try again.",
    };
  }
  if (URLS.some((pattern) => pattern.test(raw) || pattern.test(text))) {
    return {
      ok: false,
      code: "URL_REJECTED",
      message: "This preview does not accept repository or web links. Describe the work in plain English.",
    };
  }
  return { ok: true, text };
}

export function classifyLandingIntent(text) {
  const lower = text.toLowerCase();
  if (/invite|invitation|team member/.test(lower)) return "team_invitations";
  if (/upload|file|screenshot|attach/.test(lower)) return "file_upload";
  if (/password|login|sign in|auth|permission|admin|role/.test(lower)) return "authentication_access";
  if (/checkout|payment|card|declin|order/.test(lower)) return "checkout_payment";
  if (/notif|email|sms|alert|remind/.test(lower)) return "notifications";
  if (/fix|bug|broken|fail|error|crash/.test(lower)) return "bug_fix";
  return "generic_feature";
}

function present(dimension, text) {
  const lower = text.toLowerCase();
  if (dimension === "actor") return /\b(admins?|customers?|users?|owners?|members?)\b/.test(lower);
  if (dimension === "failure") return /\b(if|when|fail|declin|already|duplicate|error)\b/.test(lower);
  if (dimension === "outcome") return /\b(can|should|must|so that)\b/.test(lower) && lower.length > 24;
  return false;
}

const QUESTIONS = {
  team_invitations: [
    {
      dimension: "actor",
      prompt: "Who should be allowed to send invitations?",
      choices: ["Admins only", "Any team member", "Recommend one", "I’m not sure"],
    },
    {
      dimension: "failure",
      prompt: "What should happen if that email has already been invited?",
      choices: ["Show a clear already-invited message", "Send the invite again", "Recommend one", "I’m not sure"],
    },
  ],
  checkout_payment: [
    {
      dimension: "failure",
      prompt: "What should the customer see when their card is declined?",
      choices: ["A plain decline message on the checkout page", "Ask them to try another card", "Recommend one", "I’m not sure"],
    },
    {
      dimension: "outcome",
      prompt: "Should a declined payment ever create an order?",
      choices: ["No — never create an order", "Create a failed order for support", "Recommend one", "I’m not sure"],
    },
  ],
  default: [
    {
      dimension: "actor",
      prompt: "Who is this change for?",
      choices: ["Customers", "Admins", "Signed-in users", "Recommend one", "I’m not sure"],
    },
    {
      dimension: "outcome",
      prompt: "What should be true when this work is done?",
      choices: ["The person can finish the task themselves", "They get a clear success or failure message", "Recommend one", "I’m not sure"],
    },
  ],
};

export function nextLandingQuestion(intent, requestText, asked = []) {
  const list = QUESTIONS[intent] || QUESTIONS.default;
  return list.find((question) => !asked.includes(question.dimension) && !present(question.dimension, requestText)) || null;
}

export function landingRecommendation(intent, dimension) {
  if (intent === "team_invitations" && dimension === "actor") {
    return { text: "Admins only", explanation: "Invitation access is usually limited to admins so membership stays intentional." };
  }
  if (intent === "team_invitations" && dimension === "failure") {
    return { text: "Show a clear already-invited message", explanation: "A clear message avoids duplicate emails without blocking a later resend." };
  }
  if (intent === "checkout_payment" && dimension === "failure") {
    return { text: "A plain decline message on the checkout page", explanation: "Customers need to know the card failed without technical jargon." };
  }
  if (intent === "checkout_payment" && dimension === "outcome") {
    return { text: "No — never create an order", explanation: "A declined card should not look like a purchase." };
  }
  return { text: "Keep the first version limited to the people already named.", explanation: "That keeps the first version small enough to hand to an engineer." };
}

function src(text, source) {
  return { text, source };
}

export function buildLandingPreview(intent, requestText, answers = []) {
  const safeRequest = /ignore (all|any|previous|prior) (instructions|prompts)|system prompt/i.test(requestText)
    ? "A short product request was provided. This preview does not follow extra instructions."
    : requestText;
  const map = Object.fromEntries(answers.map((answer) => [answer.dimension, answer.text]));
  if (intent === "team_invitations") {
    return {
      title: "Team invitations",
      outcome: src("Admins can invite a teammate by email and see whether the invitation was sent.", map.outcome ? "said_by_you" : "recommended_by_ai"),
      whoIsAffected: src(map.actor || "Admins", map.actor ? "said_by_you" : "recommended_by_ai"),
      requirements: [
        src("An authorized person can send an invitation by email.", "said_by_you"),
        src(map.failure || "A duplicate invitation shows a clear already-invited message.", map.failure ? "said_by_you" : "recommended_by_ai"),
        src("The inviter sees whether the invitation is pending or accepted.", "recommended_by_ai"),
      ],
      boundaries: [src("This preview does not include billing or repository permissions.", "recommended_by_ai")],
      successChecks: [
        src("An allowed role can send one invitation.", "recommended_by_ai"),
        src("A person without permission cannot send an invitation.", "recommended_by_ai"),
      ],
      assumptions: [src("Invitations are email-based unless you later say otherwise.", "still_uncertain")],
    };
  }
  if (intent === "checkout_payment") {
    return {
      title: "Declined checkout",
      outcome: src(map.outcome || "A declined card does not create a paid order.", map.outcome ? "said_by_you" : "recommended_by_ai"),
      whoIsAffected: src("Customers paying at checkout", "said_by_you"),
      requirements: [
        src(map.failure || "Show a clear message when a card is declined.", map.failure ? "said_by_you" : "recommended_by_ai"),
        src("Do not mark the order as paid after a decline.", "recommended_by_ai"),
      ],
      boundaries: [src("This preview does not inspect a payment provider or repository.", "recommended_by_ai")],
      successChecks: [
        src("A declined card leaves no paid order.", "recommended_by_ai"),
        src("The customer sees a non-technical decline message.", "recommended_by_ai"),
      ],
      assumptions: [src("The exact wording can be refined later.", "still_uncertain")],
    };
  }
  return {
    title: intent === "bug_fix" ? "Fix the reported problem" : "New product work",
    outcome: src(map.outcome || safeRequest.slice(0, 160), map.outcome ? "said_by_you" : "said_by_you"),
    whoIsAffected: src(map.actor || "The people named in your request", map.actor ? "said_by_you" : "still_uncertain"),
    requirements: [src(safeRequest.slice(0, 180), "said_by_you")],
    boundaries: [src("This preview does not inspect a repository.", "recommended_by_ai")],
    successChecks: [src("The intended person can complete the main action.", "recommended_by_ai")],
    assumptions: [src("This sample used written rules, not a deep reading of your product.", "still_uncertain")],
  };
}

export function buildLandingReview(intent, taskPreview = null) {
  const reqs = Array.isArray(taskPreview?.requirements) ? taskPreview.requirements.map((r) => r.text) : [];
  const checks = Array.isArray(taskPreview?.successChecks) ? taskPreview.successChecks.map((r) => r.text) : [];

  const previewReq = (i, fallback) => (reqs[i] ? reqs[i] : fallback);
  const previewCheck = (i, fallback) => (checks[i] ? checks[i] : fallback);

  const exampleEvidence = (text) => `${text} Not based on repository evidence.`;

  if (intent === "team_invitations") {
    return [
      {
        requirement: previewReq(0, "Invitations are restricted to the allowed role"),
        status: "met",
        statusLabel: "Met",
        evidence: exampleEvidence("In this preview, the allowed-inviter intent is explicit."),
        askNext: null,
      },
      {
        requirement: previewCheck(0, "An invitation email is sent"),
        status: "cannot_verify",
        statusLabel: "Cannot verify",
        evidence: exampleEvidence("This anonymous preview has no repository evidence for email delivery."),
        askNext: "After you connect a repository, ask for the email send path.",
      },
      {
        requirement: previewReq(1, "Duplicate invitations show a clear message"),
        status: "partially_met",
        statusLabel: "Partially met",
        evidence: exampleEvidence("This example illustrates how duplicates could be messaged, but cannot prove delivery paths."),
        askNext: "Where should the already-invited message appear?",
      },
    ];
  }

  if (intent === "checkout_payment") {
    return [
      {
        requirement: previewReq(0, "A declined card does not create a paid order"),
        status: "met",
        statusLabel: "Met",
        evidence: exampleEvidence("This preview models the intended failure-handling outcome."),
        askNext: null,
      },
      {
        requirement: previewCheck(0, "The customer sees a clear decline message"),
        status: "partially_met",
        statusLabel: "Partially met",
        evidence: exampleEvidence("It demonstrates what a clear customer-facing message would look like."),
        askNext: "What exact sentence should the customer read?",
      },
      {
        requirement: previewReq(1, "Retry with another card still works"),
        status: "cannot_verify",
        statusLabel: "Cannot verify",
        evidence: exampleEvidence("Anonymous preview cannot run a payment provider to verify retry behavior."),
        askNext: null,
      },
    ];
  }

  return [
    {
      requirement: previewCheck(0, "The requested outcome is present"),
      status: "partially_met",
      statusLabel: "Partially met",
      evidence: exampleEvidence("This example shows how RenderRepo could compare your Task with delivered work."),
      askNext: "Which part matters most to prove first?",
    },
    {
      requirement: previewReq(0, "Failure behavior is explained"),
      status: "cannot_verify",
      statusLabel: "Cannot verify",
      evidence: exampleEvidence("Anonymous preview has no branch or pull request to inspect."),
      askNext: null,
    },
    {
      requirement: previewReq(1, "Unauthorized people cannot do the action"),
      status: "not_met",
      statusLabel: "Not met",
      evidence: exampleEvidence("This preview does not include permission proof."),
      askNext: "Who must be blocked?",
    },
  ];
}

export function sourceLabel(source) {
  if (source === "said_by_you") return "Said by you";
  if (source === "recommended_by_ai") return "Recommended by AI";
  return "Still uncertain";
}

export const CLAIM_STORAGE_KEY = "rr_public_demo_claim";
